<template>
  <div class="bg-dark p-0">
    <div class="d-flex justify-content-between">
      <button
          @click.prevent="putGoToPrevious"
          class="btn btn-secondary col-sm-3 rounded-0"
          :disabled="!previousSlide || navigationDisabled"
          key="go-to-prev">
        <AVIcon icon="chevron-left" class="me-2" />
        <span class="d-sm-inline d-none" v-text="$t('js.actions.previous')"/>
      </button>
      <template v-if="activeSlideIsBallot">
        <div class="d-flex col-sm-6 col-8 p-0">
          <AsyncButton
              v-if="ballotStateIn('new')"
              :disabled="readonly || !election.readyForVoting"
              class="btn btn-success rounded-0 col"
              key="open-slide"
              @click="putOpenBallot">
            <span v-text="$t('js.actions.open')"/>
          </AsyncButton>

          <div
              v-if="ballotStateIn('countdown')"
              class="d-flex bg-warning justify-content-end align-items-center col-6 p-0">
            <div class="d-flex flex-grow-1 justify-content-center ">
              <span v-text="$t('js.backend.live.closing')"/>
              <Countdown v-if="activeSlide.disableAt" class="ms-1" :end-time="Number(activeSlide.disableAt)"/>
            </div>
            <AsyncButton
                @click="putExtendCloseBallot(30)"
                :disabled="readonly"
                class="btn btn-secondary my-1 col-auto"
                key="extend-30"
                style="padding-top: 0.125rem; padding-bottom: 0.125rem;"
                >
              +30s
            </AsyncButton>
            <AsyncButton
                @click="putCancelCloseBallot()"
                :disabled="readonly"
                key="cancel-close"
                class="btn btn-warning rounded-0 col-auto">
              <AVIcon icon="xmark"/>
            </AsyncButton>
          </div>

          <template v-if="ballotStateIn('open')">
            <div class="btn-group col-6 px-0" key="close">
              <AsyncButton class="rounded-0 btn btn-warning" @click="putCloseBallot(0)" :disabled="readonly">
                <span v-text="$t('js.backend.live.close_now')"/>
              </AsyncButton>
              <button
                  type="button"
                  class="rounded-0 btn btn-warning dropdown-toggle dropdown-toggle-split mw-fit-content"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  key="close"
                  aria-expanded="false"
                  :disabled="readonly">
                <span class="visually-hidden" v-text="$t('js.actions.close')"/>
              </button>
              <div class="dropdown-menu dropdown-menu-end" id="closeBallotDropdown">
                <AsyncButton
                    class="dropdown-item"
                    @click="putCloseBallot(60)"
                    key="close-60">
                  <span v-text="$t('js.backend.live.close_in_n_minutes', { n: 1, plural: 1 })"/>
                </AsyncButton>
                <AsyncButton
                    class="dropdown-item"
                    key="close-180"
                    @click="putCloseBallot(180)">
                      <span v-text="$t('js.backend.live.close_in_n_minutes', { n: 3, plural: 3 })"/>
                </AsyncButton>
                <CustomCloseTimer class="dropdown-item dropdown-item-no-hover" @setCustomTimer="$time => putCloseBallot($time)" />
              </div>
            </div>
          </template>

          <template v-if="ballotStateIn('closed')">
            <AsyncButton
              class="btn btn-warning rounded-0 col-3"
              @click="putOpenBallot"
              key="reopen"
              :disabled="readonly"
            >
              <span class="d-none d-md-inline" v-text="$t('js.actions.open')" />
            </AsyncButton>

            <AsyncButton style="pointer-events: auto" v-tooltip="voteCountPositive ?  '' : $t('js.backend.live.voting_round_report.no_votes')" :disabled="!voteCountPositive"
              class="btn btn-success rounded-0 col-6"
              @click="putCalculateResult(false)"
              key="calculate">
              <span v-text="$t('js.actions.calculate')"/>
            </AsyncButton>
          </template>

          <template v-if="ballotStateIn('calculating_result')">
            <button
                class="btn btn-warning rounded-0 col"
                @click.prevent="putCancelBallot"
                key="cancel-calculate"
                :disabled="readonly">
              <span class="d-none d-md-inline" v-text="$t('js.actions.cancel')"/>
            </button>
          </template>

          <AsyncButton
              v-if="ballotStateIn('finished') && !activeSlideResultPublished"
              :disabled="readonly"
              key="make-public"
              class="btn btn-success col rounded-0"
              @click="putMakeResultPublic">
              <span class="d-none d-md-inline" v-text="$t('js.backend.live.make_public')"/>
          </AsyncButton>

          <button
              v-show="!ballotStateIn(['new', 'calculating_result'])"
              class="btn btn-danger col rounded-0"
              :disabled="readonly"
              key="reset-close-reset"
              data-bs-toggle="modal"
              data-bs-target="#resetOuter">
            <span
                class="d-none d-md-inline"
                v-text="ballotStateIn('open') ? $t('js.backend.live.close_and_reset') : $t('js.actions.reset')" />
          </button>
        </div>

      </template>
      <button
          @click.prevent="putGoToNext"
          class="btn btn-secondary rounded-0 col-sm-3"
          key="go-next"
          :disabled="!nextSlide || navigationDisabled">
        <span
          class="d-sm-inline d-none"
          v-text="$t('js.actions.next')"
        ></span>
        <AVIcon icon="chevron-right" class="ms-2" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import AsyncButton from "@/components/shared/AsyncButton.vue";
import Countdown from "@/components/frontend/shared/Countdown.vue";
import CustomCloseTimer from './CustomCloseTimer.vue';
import { mapState, mapActions } from "pinia";
import apiClient from "@/entrypoints/frontend/apiClient";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import type { ConferenceVotingRound, E2E, LiveContestState } from "@/types";

export default defineComponent({
  name: "SlideShowControls",
  components: { AsyncButton, Countdown, CustomCloseTimer },
  props: {
    navigationDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(useSharedStore, ["election"]),
    ...mapState(usePresentationStore, ["readonly", "slides", "votingRounds", "votingRoundReports", "activeSlide"]),
    firstSlide() {
      return this.slides.slice(0, 1)[0];
    },
    activeSlideResultPublished() {
      const activeSlideVotingRoundReport = this.votingRoundReports?.find((rep: E2E.VotingRoundReport) =>
        rep.votingRoundReference == this.activeSlideVotingRound.reference);
      return !!activeSlideVotingRoundReport?.publishedAt;
    },
    previousSlide() {
      if (!this.activeSlide || this.activeSlide === this.firstSlide) return undefined;
      return this.slides[this.slides.indexOf(this.activeSlide) - 1];
    },
    nextSlide() {
      if (!this.activeSlide || this.activeSlide === this.lastSlide) return undefined;

      return this.slides[this.slides.indexOf(this.activeSlide) + 1];
    },
    lastSlide() {
      return this.slides.slice(-1)[0];
    },
    activeSlideIsBallot() {
      return this.activeSlide && this.activeSlide.type === "Slides::BallotSlide";
    },
    activeSlideVotingRound() {
      if (!(this.activeSlideIsBallot && this.activeSlide.votingRoundReference)) return undefined;
      return this.votingRounds.find((vr: ConferenceVotingRound) =>
        vr.reference === this.activeSlide.votingRoundReference);
    },
    voteCountPositive() {
      return this.activeSlide.voterCountsOnClose.activeEligiblesOrVoted > 0;
    },
  },
  methods: {
    ...mapActions(usePresentationStore, ["updateStatus"]),
    ballotStateIn(ballotStates: LiveContestState | Array<LiveContestState>) {
      if (this.activeSlideIsBallot) {
        if (Array.isArray(ballotStates)) {
          return ballotStates.includes(this.activeSlide.state);
        } else {
          return ballotStates === this.activeSlide.state;
        }
      }
      return false;
    },
    async putGoToNext() {
      if (this.nextSlide) {
        await this.putGoToSlide(this.nextSlide.id);
      }
    },
    async putGoToPrevious() {
      if (this.previousSlide) {
        await this.putGoToSlide(this.previousSlide.id);
      }
    },
    async putGoToSlide(id: number) {
      if ((this.activeSlide && id === this.activeSlide.id) || this.navigationDisabled) return;
      await apiClient.put(id + "/goto");
    },
    async putOpenBallot() {
      await this.updateStatus(true); // This makes sure we update voter counts in case of pre-voting
      await apiClient.put(this.activeSlide.id + "/activate");
    },
    async putCloseBallot(offset: number = 0) {
      await apiClient.put(this.activeSlide.id + "/close", { time_offset: offset });
    },
    async putCancelCloseBallot() {
      await apiClient.put(this.activeSlide.id + "/cancel");
    },
    async putExtendCloseBallot(offset: number = 30) {
      await apiClient.put(this.activeSlide.id + "/extend", { time_offset: offset });
    },
    async putResetBallot() {
      await apiClient.put(this.activeSlide.id + "/reset");
    },
    async putCalculateResult(asPublic: boolean = true) {
      await apiClient.put(this.activeSlide.id + "/calculate", { publicised: asPublic });
    },
    async putMakeResultPublic() {
      await apiClient.put(this.activeSlide.id + "/make_public");
    },
    async putCancelBallot() {
      await apiClient.put(this.activeSlide.id + "/cancel");
    },
  },
});
</script>
